export const SILENCE_WARNING_MS = 60 * 1000;
export const SILENCE_RMS_THRESHOLD = 0.015;
export const MAX_SAMPLE_GAP_MS = 5000;

export interface SilenceState {
  quietElapsedMs: number;
  phase: 'monitoring' | 'warning';
  toastShown: boolean;
  dismissed: boolean;
  // This clock belongs to the current document and is never persisted.
  lastSampleAt: number | null;
}

export const createSilenceState = (): SilenceState => ({
  quietElapsedMs: 0, phase: 'monitoring',
  toastShown: false, dismissed: false, lastSampleAt: null,
});

export const calculateRms = (samples: Uint8Array): number => {
  if (!samples.length) return 0;
  let sum = 0;
  for (const sample of samples) sum += ((sample - 128) / 128) ** 2;
  return Math.sqrt(sum / samples.length);
};

export const suspendSilence = (state: SilenceState): SilenceState => ({
  ...state, lastSampleAt: null,
});

export const sampleSilence = (
  previous: SilenceState,
  now: number,
  rms: number | null,
  warningAfterMs = SILENCE_WARNING_MS,
): SilenceState => {
  if (rms === null || !Number.isFinite(rms) || !Number.isFinite(now)) return suspendSilence(previous);
  // Fresh sound ends the episode and permits a later warning.
  if (rms > SILENCE_RMS_THRESHOLD) return createSilenceState();
  const gap = previous.lastSampleAt === null ? 0 : now - previous.lastSampleAt;
  const elapsed = gap >= 0 && gap <= MAX_SAMPLE_GAP_MS ? gap : 0;
  const quietElapsedMs = Math.min(warningAfterMs, previous.quietElapsedMs + elapsed);
  const warning = quietElapsedMs >= warningAfterMs;
  return {
    ...previous, quietElapsedMs, lastSampleAt: now,
    phase: warning ? 'warning' : 'monitoring',
  };
};
